/**
 * ZAXIA Staff App
 * Main app component for staff portal with routing
 *
 * UI-17: Navigation Role Separation — Control Tower Architecture
 *   Control Tower is the product entry point.
 *   Investigation routes (rooms, housekeeping, requests) are secondary.
 *   Support/simulation routes are lazy-loaded and non-competing.
 *
 * No direct localStorage access - all through useAuth hook
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useState } from 'react';
import { StaffDataProcessingNotice, hasStaffDismissedNotice } from './components/StaffDataProcessingNotice';
import {
  LoginPage,
  RoomOperationsPage,
  HousekeepingPage,
  RequestsPage,
  ControlTowerPage,
  RoomViewPage,
  GuestViewPage,
  RequestViewPage,
} from './pages';
import { ProtectedRoute } from './components';
import { StaffLayout } from './layout';
import { useAuth } from './hooks/useAuth';

// Lazy-loaded non-primary routes (secondary, tools, system)
const BookingsPage = lazy(() => import('./pages/BookingsPage').then(m => ({ default: m.BookingsPage })));
const GreenStayPage = lazy(() => import('./pages/GreenStayPage').then(m => ({ default: m.GreenStayPage })));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage').then(m => ({ default: m.AIAssistantPage })));
const PMSSandboxPage = lazy(() => import('./pages/PMSSandboxPage').then(m => ({ default: m.PMSSandboxPage })));
const OpsSandboxPage = lazy(() => import('./pages/OpsSandboxPage').then(m => ({ default: m.OpsSandboxPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const OperationalFeedPage = lazy(() => import('./pages/OperationalFeedPage').then(m => ({ default: m.OperationalFeedPage })));
const GuidePage = lazy(() => import('./pages/GuidePage').then(m => ({ default: m.GuidePage })));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center p-12">
      <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export function StaffApp() {
  const { isAuthenticated, isLoading } = useAuth();
  // PILOT-3: Staff data processing notice (shown once after first login)
  const [noticeShown, setNoticeShown] = useState(() => hasStaffDismissedNotice());

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated && !noticeShown && (
        <StaffDataProcessingNotice onDismiss={() => setNoticeShown(true)} />
      )}
      <BrowserRouter basename="/staff.html">
      <Routes>
        {/* Public Route: Login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/control-tower" replace /> : <LoginPage />
          }
        />

        {/* Protected Routes: Dashboard */}
        <Route
          element={
            <ProtectedRoute>
              <StaffLayout />
            </ProtectedRoute>
          }
        >
          {/* PRIMARY — Control Tower is the product */}
          <Route path="/control-tower" element={<ControlTowerPage />} />

          {/* OPS-36 — Mobile operational guidance feed */}
          <Route path="/ops-feed" element={<Suspense fallback={<LazyFallback />}><OperationalFeedPage /></Suspense>} />

          {/* INVESTIGATION — Secondary drill-down surfaces */}
          <Route path="/rooms" element={<RoomOperationsPage />} />
          <Route path="/housekeeping" element={<HousekeepingPage />} />
          <Route path="/requests" element={<RequestsPage />} />

          {/* Entity Deep Views (drill-down targets) */}
          <Route path="/rooms/:roomId" element={<RoomViewPage />} />
          <Route path="/guests/:guestId" element={<GuestViewPage />} />
          <Route path="/requests/:requestId" element={<RequestViewPage />} />

          {/* SUPPORT — Tools / Simulation */}
          <Route path="/bookings" element={<Suspense fallback={<LazyFallback />}><BookingsPage /></Suspense>} />
          <Route path="/green" element={<Suspense fallback={<LazyFallback />}><GreenStayPage /></Suspense>} />
          <Route path="/ai-assistant" element={<Suspense fallback={<LazyFallback />}><AIAssistantPage /></Suspense>} />
          <Route path="/pms-sandbox" element={<Suspense fallback={<LazyFallback />}><PMSSandboxPage /></Suspense>} />
          <Route path="/ops/sandbox" element={<Suspense fallback={<LazyFallback />}><OpsSandboxPage /></Suspense>} />

          {/* SYSTEM */}
          <Route path="/settings" element={<Suspense fallback={<LazyFallback />}><SettingsPage /></Suspense>} />
          <Route path="/guide" element={<Suspense fallback={<LazyFallback />}><GuidePage /></Suspense>} />

          {/* Backward-compat redirects */}
          <Route path="/dashboard" element={<Navigate to="/control-tower" replace />} />
          <Route path="/room-operations" element={<Navigate to="/rooms" replace />} />
          <Route path="/maintenance" element={<Navigate to="/requests" replace />} />
          <Route path="/messaging" element={<Navigate to="/ai-assistant" replace />} />
        </Route>

        {/* Default Redirect */}
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? '/control-tower' : '/login'} replace />
          }
        />
      </Routes>
    </BrowserRouter>
    </>
  );
}
